/**
 * @file
 * This file contains the helper functions to create sagas and reducers for redux toolkit.
 * It also contains the helper functions to add sagas to the store.
 * @summary
 * This file exports the following functions:
 * - `addSaga`: This function adds a saga to the store.
 * - `createSagaReducer`: This function creates a reducer for the given slice.
 * - `createAsyncSaga`: This function creates a saga for the given slice.
 * @exports
 * - `sagaStore`: This object contains the list of sagas.
 * - `addSaga`: This function adds a saga to the store.
 * - `createSagaReducer`: This function creates a reducer for the given slice.
 * - `createAsyncSaga`: This function creates a saga for the given slice.
 * @requires
 * - `redux-saga/effects`: This module is used to create the sagas.xww
 */

import { State } from "@dnd-kit/core/dist/store";
import { Slice, Store } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import { call, put, debounce } from "redux-saga/effects";
import debounceEffect from "lodash/debounce";
import * as Sentry from "@sentry/react";

/**
 * Object that contains the list of sagas.
 */
export const sagaStore: any = {
  list: [],
};

/**
 * Adds a saga to the store.
 * @param name - The name of the saga.
 * @param saga - The saga function.
 * @param debounceMs - Optional debounce time in milliseconds. Default is 300ms.
 */
export const addSaga = (name: string, saga: any, debounceMs: number = 300) => {
  sagaStore.list.push(debounce(debounceMs, name, saga));
};

/**
 * Interface for a saga slice.
 */
export interface sagaSlice extends Slice {
  actions: any;
}

/**
 * Creates a reducer for the given slice.
 * @param name - The name of the reducer.
 * @param stateKey - The key of the state in the reducer.
 * @param method - Optional object containing pending, success, and failure methods.
 * @returns The reducer object.
 */
export const createSagaReducer = (
  name: string,
  stateKey: string,
  method: any = {}
) => {
  type State = any;
  type Action = { type: string; payload: any } | void;

  return {
    [name]: (state: State, action: Action) => {
      state[stateKey].loading = true;
      state[stateKey].data = {};
      state[stateKey].error = null;
      method?.pending?.(state, action);
    },
    [name + "/success"]: (state: State, action: Action) => {
      state[stateKey].loading = false;
      state[stateKey].data = action?.payload;
      method?.success?.(state, action);
    },
    [name + "/failure"]: (state: State, action: Action) => {
      state[stateKey].loading = false;
      state[stateKey].data = {};
      state[stateKey].error = action?.payload;
      method?.failure?.(state, action);
    },
  };
};

/**
 * Creates a saga for the given slice.
 * @param name - The name of the saga.
 * @param slice - The slice object.
 * @param service - The service function.
 * @param params - Optional array of parameter names.
 * @returns The saga function.
 */
export const createAsyncSaga = (
  name: string,
  slice: any,
  service: any,
  params: string[] = []
) => {
  return function* fetchSaga(action: any): Generator<any> {
    const payload = action.payload;
    try {
      const response: any = yield call(() =>
        service(...params.map((param: string) => payload[param]))
      );
      yield put(
        slice.actions[`${name}/success`](
          JSON.parse(JSON.stringify(response?.data))
        )
      );
    } catch (error: AxiosError | any) {
      Sentry.captureException(error);
      const err = {
        message:
          error?.response?.data?.message ||
          error?.message ||
          "Something went wrong.",
        status: error?.response?.status,
        data: error?.response?.data,
        code: error?.code,
      };
      yield put(
        slice.actions[`${name}/failure`](JSON.parse(JSON.stringify(err)))
      );
    }
  };
};

const persistList: string[] = [];

/**
 * Recursively merges the `source` object into the `target` object.
 * New keys from `source` are added to `target`.
 * For existing keys, values from `target` are preserved. Only nested objects are merged recursively.
 * This is designed to add new keys from an initial state to a persisted state without overwriting user data.
 * @param target - The target object (e.g., persisted state).
 * @param source - The source object (e.g., initial state with new keys).
 * @returns The merged object.
 */
const deepMerge = (target: any, source: any): any => {
  if (target === null || target === undefined) {
    return source;
  }

  if (source === null || source === undefined) {
    return target;
  }

  // For primitives and arrays, we prioritize the persisted value (target).
  if (
    typeof target !== "object" ||
    typeof source !== "object" ||
    Array.isArray(target) ||
    Array.isArray(source)
  ) {
    return target;
  }

  // Both are mergable objects.
  const merged = { ...target };
  for (const key in source) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      if (key in merged) {
        merged[key] = deepMerge(merged[key], source[key]);
      } else {
        merged[key] = source[key];
      }
    }
  }
  return merged;
};

export const persist = (
  name: string,
  state: any
): { name: string; initialState: State } => {
  try {
    const key = "persist:" + name;
    persistList.push(name);
    if (key in localStorage) {
      const serializedState = localStorage.getItem(key);
      if (serializedState === null) {
        return {
          name,
          initialState: state,
        };
      }
      const persistedState = JSON.parse(serializedState);
      // Merge persisted state with initial state to ensure new keys are added
      const mergedState = deepMerge(persistedState, state);

      // Update localStorage with merged state to include new keys
      const updatedSerializedState = JSON.stringify(mergedState);
      localStorage.setItem(key, updatedSerializedState);

      return {
        name,
        initialState: mergedState,
      };
    } else {
      const serializedState = JSON.stringify(state);
      localStorage.setItem(key, serializedState);

      return {
        name,
        initialState: state,
      };
    }
  } catch {
    return {
      name,
      initialState: state,
    };
  }
};

export const watchStore = (store: Store) => {
  store.subscribe(
    debounceEffect(() => {
      const state = store.getState();
      if (persistList.length > 0) {
        persistList.forEach((name) => {
          const key = "persist:" + name;
          const serializedState = JSON.stringify((state as any)[name]);
          localStorage.setItem(key, serializedState);
        });
      }
    }, 300)
  );
};

/**
 * Clears all persisted Redux state from localStorage.
 * This should be called when the user logs out to ensure no user data persists.
 */
export const clearPersistedState = () => {
  persistList.forEach((name) => {
    const key = "persist:" + name;
    localStorage.removeItem(key);
  });
};
